import { ChevronRight, Camera, AlertCircle } from "lucide-react";
import { trpc } from "@/lib/trpc";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { Tonal } from "@/components/ui/tonal";
import { Kicker } from "@/components/ui/kicker";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { cn } from "@/lib/utils";
import { useState } from "react";
import { StepScreenshot } from "@/components/walkthrough/step-screenshot";
import {
  toneBorderClass,
  toneDotClass,
  toneTextClass,
} from "@/components/experience/vocabulary";

type OutcomeName = "resolved" | "persists" | "regressed" | "uncertain";

type OutcomeTone = "helps" | "muted" | "critical" | "important";

interface OutcomeRow {
  outcomeId: string;
  findingId: string;
  outcome: OutcomeName;
  type: string;
  severity: string | null;
  description: string;
  recommendation: string | null;
  evidence: string | null;
  evidenceStepId: string | null;
}

interface NewObservation {
  id: string;
  type: string;
  severity: string | null;
  description: string;
  recommendation: string | null;
  walkthroughStepId: string | null;
}

interface VerificationOutcomesListProps {
  walkthroughId: string;
  /** Verification walkthrough steps — used to resolve evidenceStepId → stepNumber for screenshots. */
  steps: Array<{ id: string; stepNumber: number }>;
}

const OUTCOME_ORDER: OutcomeName[] = [
  "regressed",
  "persists",
  "uncertain",
  "resolved",
];

const OUTCOME_CONFIG: Record<
  OutcomeName,
  { label: string; tone: OutcomeTone; emptyCopy: string }
> = {
  regressed: {
    label: "Regressed",
    tone: "critical",
    emptyCopy: "Nothing broke that used to work.",
  },
  persists: {
    label: "Still there",
    tone: "muted",
    emptyCopy: "No findings carried over.",
  },
  uncertain: {
    label: "Uncertain",
    tone: "important",
    emptyCopy: "Every baseline finding got a verdict.",
  },
  resolved: {
    label: "Resolved",
    tone: "helps",
    emptyCopy: "Nothing resolved on this run.",
  },
};

export function VerificationOutcomesList({
  walkthroughId,
  steps,
}: VerificationOutcomesListProps) {
  const { data, isLoading, error, refetch } =
    trpc.scenario.getVerificationResults.useQuery({ walkthroughId });

  const stepNumberById = new Map(steps.map((s) => [s.id, s.stepNumber]));

  if (isLoading) {
    return (
      <section className="space-y-6" aria-label="Verification outcomes">
        <Kicker as="h2">The rerun</Kicker>
        <Skeleton className="h-20 w-full" />
        <Skeleton className="h-20 w-full" />
        <Skeleton className="h-20 w-full" />
        <Skeleton className="h-20 w-full" />
      </section>
    );
  }

  if (error || !data) {
    // Explicit fallback so a failed fetch doesn't render the page empty with
    // no signal that something went wrong — this section replaces the full
    // findings UI on verification walkthroughs.
    return (
      <section className="space-y-4" aria-label="Verification outcomes">
        <Kicker as="h2">The rerun</Kicker>
        <Tonal padding="md" className="flex items-start gap-3">
          <AlertCircle
            className="mt-0.5 h-4 w-4 shrink-0 text-severity-critical"
            aria-hidden="true"
          />
          <div className="space-y-2">
            <p className="text-base font-medium">
              Couldn't load rerun outcomes.
            </p>
            <p className="text-sm text-muted-foreground">
              We encountered an issue retrieving the outcome comparisons for
              this rerun.
            </p>
            <Button variant="outline" size="sm" onClick={() => refetch()}>
              Try again
            </Button>
          </div>
        </Tonal>
      </section>
    );
  }

  const totalBaseline =
    data.counts.resolved +
    data.counts.persists +
    data.counts.regressed +
    data.counts.uncertain;

  // Auto-open the most important non-empty bucket so the user lands on the
  // signal that matters most: regressions > persistence > uncertainty >
  // resolved. If every bucket is empty (baseline had zero findings) none open.
  const firstOpen = OUTCOME_ORDER.find(
    (name) => (data[name] as OutcomeRow[]).length > 0,
  );

  return (
    <section className="space-y-4" aria-label="Verification outcomes">
      <header className="space-y-1">
        <Kicker as="h2">The rerun</Kicker>
        <p className="text-base tabular-nums">
          Re-checked {totalBaseline} baseline finding
          {totalBaseline !== 1 ? "s" : ""}
          {data.counts.newObservations > 0
            ? ` · noted ${data.counts.newObservations} new observation${
                data.counts.newObservations !== 1 ? "s" : ""
              }`
            : ""}
          .
        </p>
      </header>

      <div className="space-y-2">
        {OUTCOME_ORDER.map((outcome) => {
          const rows = data[outcome] as OutcomeRow[];
          return (
            <OutcomeBucket
              key={outcome}
              outcome={outcome}
              rows={rows}
              walkthroughId={walkthroughId}
              stepNumberById={stepNumberById}
              defaultOpen={outcome === firstOpen}
            />
          );
        })}
      </div>

      {data.newObservations.length > 0 && (
        <NewObservationsSection
          observations={data.newObservations as NewObservation[]}
          walkthroughId={walkthroughId}
          stepNumberById={stepNumberById}
        />
      )}
    </section>
  );
}

function OutcomeBucket({
  outcome,
  rows,
  walkthroughId,
  stepNumberById,
  defaultOpen,
}: {
  outcome: OutcomeName;
  rows: OutcomeRow[];
  walkthroughId: string;
  stepNumberById: Map<string, number>;
  defaultOpen: boolean;
}) {
  const config = OUTCOME_CONFIG[outcome];
  const tone = config.tone;
  const count = rows.length;
  const empty = count === 0;

  return (
    <Collapsible defaultOpen={defaultOpen && !empty}>
      <article
        className={cn(
          "overflow-hidden rounded-lg border-l-[3px] bg-surface-tonal transition-colors",
          toneBorderClass(tone),
        )}
        aria-label={config.label}
      >
        <CollapsibleTrigger
          className={cn(
            "group flex w-full items-center gap-3 px-4 py-3 text-left",
            !empty && "hover:bg-muted/40",
            empty && "cursor-default",
          )}
          disabled={empty}
        >
          <ChevronRight
            aria-hidden="true"
            className={cn(
              "h-4 w-4 shrink-0 text-muted-foreground transition-transform duration-200",
              "group-data-[state=open]:rotate-90",
              empty && "invisible",
            )}
          />
          <span
            aria-hidden="true"
            className={cn(
              "inline-block size-2 shrink-0 rounded-full",
              toneDotClass(tone),
            )}
          />
          <span
            className={cn(
              "text-base font-semibold",
              empty ? "text-muted-foreground" : toneTextClass(tone),
            )}
          >
            {config.label}
          </span>
          <output
            className={cn(
              "ml-auto text-base font-semibold tabular-nums",
              empty ? "text-muted-foreground" : toneTextClass(tone),
            )}
          >
            {count}
          </output>
        </CollapsibleTrigger>

        {empty && (
          <p className="-mt-1 pb-3 pl-16 pr-4 text-sm text-muted-foreground">
            {config.emptyCopy}
          </p>
        )}

        {!empty && (
          <CollapsibleContent>
            <ul className="divide-y divide-border/60 border-t border-border/60">
              {rows.map((row) => (
                <OutcomeRowItem
                  key={row.outcomeId}
                  row={row}
                  walkthroughId={walkthroughId}
                  stepNumberById={stepNumberById}
                />
              ))}
            </ul>
          </CollapsibleContent>
        )}
      </article>
    </Collapsible>
  );
}

function OutcomeRowItem({
  row,
  walkthroughId,
  stepNumberById,
}: {
  row: OutcomeRow;
  walkthroughId: string;
  stepNumberById: Map<string, number>;
}) {
  const stepNumber = row.evidenceStepId
    ? stepNumberById.get(row.evidenceStepId)
    : undefined;

  return (
    <li className="space-y-3 px-4 py-3">
      <p className="text-base text-foreground">{row.description}</p>
      {(row.evidence || stepNumber !== undefined) && (
        <div className="space-y-2 border-l-2 border-border pl-4">
          <Kicker>Rerun note</Kicker>
          {row.evidence && (
            <p className="text-sm text-muted-foreground">{row.evidence}</p>
          )}
          {stepNumber !== undefined && (
            <ScreenshotLink
              walkthroughId={walkthroughId}
              stepNumber={stepNumber}
              defaultOpen={
                row.outcome === "regressed" || row.outcome === "persists"
              }
            />
          )}
        </div>
      )}
    </li>
  );
}

function ScreenshotLink({
  walkthroughId,
  stepNumber,
  defaultOpen = false,
}: {
  walkthroughId: string;
  stepNumber: number;
  defaultOpen?: boolean;
}) {
  const [open, setOpen] = useState(defaultOpen);

  return (
    <div>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center gap-1.5 text-sm text-muted-foreground underline-offset-4 transition-colors hover:text-foreground hover:underline"
        aria-expanded={open}
      >
        <Camera className="h-3 w-3" aria-hidden="true" />
        {open ? "Hide screenshot" : `Screenshot · step ${stepNumber}`}
      </button>
      {open && (
        <div className="mt-3 overflow-hidden rounded-lg border border-border/60 bg-muted/30">
          <StepScreenshot
            walkthroughId={walkthroughId}
            stepNumber={stepNumber}
            alt={`Step ${stepNumber} evidence`}
          />
        </div>
      )}
    </div>
  );
}

function NewObservationsSection({
  observations,
  walkthroughId,
  stepNumberById,
}: {
  observations: NewObservation[];
  walkthroughId: string;
  stepNumberById: Map<string, number>;
}) {
  return (
    <section className="space-y-3 border-t border-border/60 pt-6">
      <div className="flex items-baseline gap-3">
        <Kicker as="h3">New this run</Kicker>
        <span className="text-sm text-muted-foreground">
          Not in the baseline — observed for the first time
        </span>
      </div>
      <ul className="divide-y divide-border/60 overflow-hidden rounded-lg border border-border/60 bg-surface-tonal">
        {observations.map((obs) => {
          const stepNumber = obs.walkthroughStepId
            ? stepNumberById.get(obs.walkthroughStepId)
            : undefined;
          return (
            <li key={obs.id} className="space-y-2 px-4 py-3">
              <p className="text-base">{obs.description}</p>
              {stepNumber !== undefined && (
                <div className="border-l-2 border-border pl-4">
                  <ScreenshotLink
                    walkthroughId={walkthroughId}
                    stepNumber={stepNumber}
                  />
                </div>
              )}
            </li>
          );
        })}
      </ul>
    </section>
  );
}
